import { useLoaderData, useSearchParams } from 'react-router';
import { useFilterDrawerPreference } from '@modules/search/hook/use-filter-drawer-preference';
import { useAppDisplayModeContext } from '@archipro-design/aria';

import { Spinner } from '@rocco/components/spinner';
import { cn } from '@rocco/utils/cn';
import type { loader as networkLoader } from '@modules/professional/page/loaders/network-page-loader.server';
import { NoSearchResults } from '@modules/professional/component/shared/NoSearchResults';
import { CollapsiblePageLayout } from '~/modules/category/component/collapsible-page-layout/CollapsiblePageLayout';
import { PROFESSIONAL_TAB_PROFESSIONAL_FILTERS_CONFIG } from '@modules/professional/config/professional-tab-filter-configs';
import { ProfessionalTabListingBar } from './ProfessionalTabListingBar';
import { useInPageSearchTerm } from '../hooks/use-in-page-search-term';
import { SearchFilter } from '@modules/professional/component/shared/SearchFilter';
import { DirectoryDisplayDropdown } from '~/ui-rocco/pages/directory-browse/DirectoryDisplayDropdown';
import { ProfessionalTabFilterSidebar } from './ProfessionalTabFilterSidebar';
import { ProfessionalAppliedFilters } from './ProfessionalAppliedFilters';
import { resetFilterStates } from '~/modules/search/util/reset-filter-states';
import { useSearchFilters } from '@modules/search/hook/use-search-filters';
import { useMatchesHandleData } from '~/modules/root/hook';
import type { SearchIndex } from '~/modules/search/type';
import {
    IN_PAGE_SEARCH_IGNORE_FILTER_KEYS,
    IN_PAGE_SEARCH_PARAM_KEY,
} from '~/modules/professional/config/constants';
import { useProfessionalInitialRouteData } from '@modules/professional/hook/use-professional-initial-route-data';
import { useProfileFamily } from '@modules/professional/hook/use-profile-family';
import {
    pageStartsForHydratedPages,
    useTabLoadMore,
} from '@modules/professional/hook/use-tab-load-more';
import { useProfessionalHybridLoad } from '@modules/professional/hook/use-professional-hybrid-load';
import {
    useViewportPageSync,
    viewportPageMarkerProps,
} from '~/ui-rocco/hooks/use-viewport-page-sync';
import { LoadMoreButton } from '~/ui-rocco/pages/detail-shared';
import type { NetworkProfessional } from '@modules/professional/util/map-es-tab-results';
import { CategoryPageTileWithAdminActions } from '~/ui-rocco/pages/category-page/CategoryPageTileWithAdminActions';
import { SectionLabel } from '~/ui-rocco/pages/home-page/components/SectionLabel';
import {
    DIRECTORY_DESKTOP_COL_CLASSES,
    useDirectoryDisplayColumns,
} from '~/ui-rocco/pages/directory-browse/directory-display-columns';
import { ProfessionalBrandFamilyGrid } from './ProfessionalBrandFamilyGrid';
import {
    TAB_RESULTS_PENDING_CLASS,
    useTabResultsPending,
} from '../utils/use-tab-results-pending';
import {
    MOBILE_SECTION_LABEL,
    TAB_BAR_GRID_GAP,
    TAB_CONTENT_GAP,
} from '../utils/tab-spacing';

/**
 * Network tab (/partners) body — an "(Our Brand Family)" grid when the profile
 * belongs to a network, then an "(All Connections)" divider, the collapsible
 * left filters reused from the legacy tab and a 4-column grid of partner-
 * professional tiles (portrait image + corner logo badge + name + category),
 * matching the Figma (5522:95930). Driven by the network ES loader (tiles +
 * pagination + filters), with infinite scroll appending further pages.
 * Self-hides when there are no connections, no filters and no family; the name /
 * stats / CTA sit in the persistent profile header.
 */
export const ProfessionalNetworkGridSection = () => {
    const {
        networkProfessionals,
        networkHasMore,
        pagination,
        filterStates,
        filterStatesRocco,
        aggregateScopeFilters,
    } = useLoaderData<typeof networkLoader>();
    const { URLSegment } = useProfessionalInitialRouteData();
    const { desktop } = useAppDisplayModeContext();
    const family = useProfileFamily();

    const { filterDrawerOpen, toggleFilterDrawer } =
        useFilterDrawerPreference();

    // Keyed separately from the `professionals` browse grid so a choice made
    // here does not follow the reader out to /professionals. Five across by
    // default: a practice's network is a list to scan rather than a set of
    // pictures to dwell on, and four left the tab looking emptier than it is.
    const { columns, setColumns } = useDirectoryDisplayColumns(
        'professional-network',
        5,
    );

    // The sidebar and the sort both navigate; without this the grid sits
    // unchanged for the round trip and a click reads as ignored.
    const resultsPending = useTabResultsPending();
    const [searchParams] = useSearchParams();

    const {
        items: professionals,
        hasMore,
        isLoading,
        loadMore,
        pageStartByIndex,
    } = useTabLoadMore({
        initialItems: networkProfessionals as NetworkProfessional[],
        initialHasMore: networkHasMore,
        initialPage: pagination.page,
        urlSegment: URLSegment,
        searchType: 'professional',
        getKey: (pro) => pro.model.link,
        // A ?page=N deep link hydrates pages 1..N in one loader query, so
        // the initial items span N pages, each needing its own boundary.
        initialPageStartByIndex: pageStartsForHydratedPages(
            pagination.page,
            pagination.pageSize,
        ),
    });

    // `?page=N` follows the page of tiles actually in view, scrolling either
    // direction — not the pages the infinite-scroll lookahead has fetched.
    useViewportPageSync({
        enabled: hasMore || Object.keys(pageStartByIndex).length > 1,
    });

    const { showViewMore, onViewMore, infiniteEnabled, sentinelRef } =
        useProfessionalHybridLoad({
            hasMore,
            isLoading,
            loadMore,
            resetKey: searchParams.toString(),
        });

    const category = useMatchesHandleData<SearchIndex>(
        'category',
        'professional',
    );
    // The desktop bar's term, in the URL the tab's loader reads.
    const [searchTerm, setSearchTerm] = useInPageSearchTerm();
    const searchFilters = useSearchFilters({
        category,
        initialStates: filterStates,
        isCategoryPage: false,
        ignoreFilterKeys: IN_PAGE_SEARCH_IGNORE_FILTER_KEYS,
    });

    const hasAppliedFilters =
        !!searchFilters.appliedFilters &&
        searchFilters.appliedFilters.length > 0;

    // A search term lives in the `q` param, not in appliedFilters — so a
    // 0-result search must also keep the section mounted, otherwise the
    // search bar disappears and there's no way to clear it.
    const hasActiveSearch = !!searchParams.get(IN_PAGE_SEARCH_PARAM_KEY);

    // A family is worth the tab on its own, even with no wider connections.
    if (
        professionals.length === 0 &&
        !hasAppliedFilters &&
        !hasActiveSearch &&
        !family
    )
        return null;

    return (
        <section className="w-full overflow-x-clip bg-ground text-[#1f1f21]">
            {/* Desktop keeps the editorial title + intro + CTA cluster; on mobile
                the persistent social-profile header carries the identity/CTAs and
                the tab goes straight to results. */}
            {desktop && (
                <>
                    <div
                        className={cn(
                            'mx-auto w-full max-w-(--ap-page-width) md:px-[3.132rem]',
                            TAB_CONTENT_GAP,
                        )}
                    >
                        {/* The heading repeated the tab pill above it; kept as sr-only
                            so the tab still has a heading in the document outline. */}
                        <h2 className="sr-only">Network</h2>
                    </div>
                </>
            )}
            {/* The brand family leads the tab: a visitor who landed on one brand
                meets its siblings before the wider network (Figma 5522:95930).
                Kept outside the desktop-only block — on mobile it's the only
                place the family appears on this tab. */}
            {family ? (
                <div className="mx-auto w-full max-w-(--ap-page-width) max-md:px-6 max-md:pt-[2.4rem] md:px-[3.132rem]">
                    {/* Desktop only: on mobile this is the first thing under the
                        tab strip, whose own underline already closes off the nav,
                        so a second rule reads as a stray line. The one above
                        "(All Connections)" stays — that one separates two groups. */}
                    <div className="h-px w-full bg-black/20 max-md:hidden" />
                    <SectionLabel
                        className={cn(
                            // The offset pairs the label with the rule above it;
                            // on mobile that rule is hidden, so it would just
                            // push the tab's first block off the 20px other tabs
                            // open with.
                            'mt-[1.062rem] block text-[#181818] max-md:mt-0 md:text-[2rem]',
                            MOBILE_SECTION_LABEL,
                        )}
                    >
                        (Our Brand Family)
                    </SectionLabel>
                    <ProfessionalBrandFamilyGrid className="max-md:pt-[2.4rem] md:pt-[2.4rem]" />
                </div>
            ) : null}
            {/* On mobile the divider only earns its place once the family grid
                sits above it and the two groups need telling apart. */}
            {desktop || family ? (
                <div
                    className={cn(
                        'mx-auto w-full max-w-(--ap-page-width) max-md:px-6 max-md:pt-10 md:px-[3.132rem]',
                        // With no family above it this is the tab's first
                        // block, and the shared tab gap is the whole run.
                        family && 'md:pt-[8.125rem]',
                    )}
                >
                    <div className="h-px w-full bg-black/20" />
                </div>
            ) : null}
            {/* Filters + grid, straight to results (the tab pill names the page,
                the persistent header carries the identity). Default light theme. */}
            <div className="mx-auto w-full max-w-(--ap-page-width) max-md:px-6 max-md:pt-3 max-md:pb-14 md:px-[3.132rem] md:pt-[1.5rem] md:pb-[7.5rem]">
                <CollapsiblePageLayout
                    paddingClassName="px-0"
                    sidePanel={
                        desktop &&
                        filterStatesRocco && (
                            <div className="h-full md:pt-[1.8rem]">
                                <ProfessionalTabFilterSidebar
                                    searchIndex="professional"
                                    config={
                                        PROFESSIONAL_TAB_PROFESSIONAL_FILTERS_CONFIG
                                    }
                                    initialFilterStates={filterStatesRocco}
                                    aggregateScopeFilters={
                                        aggregateScopeFilters
                                    }
                                />
                            </div>
                        )
                    }
                    hideSidePanel={!filterDrawerOpen}
                >
                    {desktop ? (
                        // The listing tabs' bar: filter, search, and Display at
                        // the row's end.
                        <ProfessionalTabListingBar
                            search={{
                                value: searchTerm,
                                onChange: setSearchTerm,
                                placeholder: 'Search Network',
                            }}
                            filterDrawer={{
                                open: filterDrawerOpen,
                                onToggle: toggleFilterDrawer,
                            }}
                            filtersCount={
                                searchFilters.appliedFilters?.length ?? 0
                            }
                            display={
                                <DirectoryDisplayDropdown
                                    columns={columns}
                                    onColumnsChange={setColumns}
                                />
                            }
                        />
                    ) : (
                        <SearchFilter
                            searchFilters={searchFilters}
                            filterDrawerOpen={filterDrawerOpen}
                            toggleFilterDrawer={toggleFilterDrawer}
                            searchBarVariant="revamp"
                            mobileSearchPlaceholder="Search Network"
                            hideSort
                            filterControl="plain"
                        />
                    )}
                    {/* The applied-filter bar is desktop-only; on mobile the
                        Refine button's badge + sheet already surface these. */}
                    {desktop && hasAppliedFilters && (
                        <ProfessionalAppliedFilters
                            filterItems={searchFilters.appliedFilters ?? []}
                            theme="light"
                            onClearAll={() =>
                                searchFilters.resetFilters(
                                    resetFilterStates(
                                        searchFilters.filterStates,
                                        IN_PAGE_SEARCH_IGNORE_FILTER_KEYS,
                                    ),
                                )
                            }
                        />
                    )}
                    {/* Wraps the empty state as well as the grid, so a filter
                        applied over zero results still reads as acknowledged,
                        and holds the paging controls inside the pending state. */}
                    <div
                        className={cn(
                            'transition-opacity duration-200',
                            resultsPending && TAB_RESULTS_PENDING_CLASS,
                        )}
                    >
                        {professionals.length === 0 ? (
                            <NoSearchResults resultsType={'professionals'} />
                        ) : (
                            <>
                                <div
                                    className={cn(
                                        'grid grid-cols-2 gap-x-2 gap-y-8 max-md:pt-9 md:gap-x-3 md:gap-y-[6.25rem]',
                                        TAB_BAR_GRID_GAP,
                                        DIRECTORY_DESKTOP_COL_CLASSES[columns],
                                    )}
                                >
                                    {professionals.map((pro, index) => {
                                        // The tile /professionals renders, from
                                        // the view model the search mapper
                                        // already builds.
                                        const card = (
                                            <CategoryPageTileWithAdminActions
                                                key={`${pro.model.link}-${index}`}
                                                tile={pro}
                                            />
                                        );
                                        // The page marker rides a wrapper, as
                                        // it does on the Products and Projects
                                        // grids — the tile does not forward
                                        // arbitrary props.
                                        const startPage =
                                            pageStartByIndex[index];
                                        return startPage === undefined ? (
                                            card
                                        ) : (
                                            <div
                                                key={`${pro.model.link}-${index}`}
                                                {...viewportPageMarkerProps(
                                                    startPage,
                                                )}
                                            >
                                                {card}
                                            </div>
                                        );
                                    })}
                                </div>
                                {infiniteEnabled &&
                                    hasMore &&
                                    !resultsPending && (
                                        <div
                                            ref={sentinelRef}
                                            aria-hidden="true"
                                            style={{ height: 1 }}
                                        />
                                    )}
                                {showViewMore && (
                                    <LoadMoreButton
                                        onClick={onViewMore}
                                        isLoading={resultsPending}
                                        label="View more"
                                        className="pt-[3rem]"
                                    />
                                )}
                                {isLoading && (
                                    <div className="flex justify-center pt-[3rem]">
                                        <Spinner className="size-8 text-[#181818]" />
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </CollapsiblePageLayout>
            </div>
        </section>
    );
};
